import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';

// Full driver ↔ vehicle assignment history, shown in its own popup from the
// Driver Vehicle Mapping page. Filter by driver, vehicle, status and date range, with pagination.
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const fmtTime = (d) => d ? new Date(d).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '';
const duration = (start, end) => {
    const ms = (end ? new Date(end) : new Date()) - new Date(start);
    if (!(ms > 0)) return '—';
    const days = Math.floor(ms / 86400000);
    if (days >= 30) { const mo = Math.floor(days / 30); const rd = days % 30; return `${mo} mo${rd ? ` ${rd} d` : ''}`; }
    if (days >= 1) return `${days} day${days > 1 ? 's' : ''}`;
    const hrs = Math.floor(ms / 3600000);
    return hrs >= 1 ? `${hrs} hr${hrs > 1 ? 's' : ''}` : '< 1 hr';
};
const vLabel = (v) => v ? `${v.vehicleCode ? v.vehicleCode + ' · ' : ''}${v.plateNumber || ''}` : '';
const dLabel = (d) => d ? `${d.name || ''}${d.driverCode ? ` (${d.driverCode})` : ''}` : '';
const idOf = (x) => String(x?._id || x || '');

// Page numbers with ellipses: 1 … 4 5 6 … 12
const pageList = (page, total) => {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const out = [1];
    const from = Math.max(2, page - 1), to = Math.min(total - 1, page + 1);
    if (from > 2) out.push('…');
    for (let i = from; i <= to; i++) out.push(i);
    if (to < total - 1) out.push('…');
    out.push(total);
    return out;
};

export default function AssignmentHistory({ user, initialDriverId = '', onBack }) {
    const operatorId = user?.operatorId || user?._id || 'all';
    const [drivers, setDrivers] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [driverF, setDriverF] = useState(initialDriverId || '');
    const [vehicleF, setVehicleF] = useState('');
    const [statusF, setStatusF] = useState('');
    const [fromF, setFromF] = useState('');
    const [toF, setToF] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const load = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`/api/driver-vehicle-mapping-history/${operatorId || 'all'}`);
            setRows(Array.isArray(res.data) ? res.data : []);
        } catch (e) { console.error('History load error', e); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [operatorId]);
    useEffect(() => { setDriverF(initialDriverId || ''); setPage(1); }, [initialDriverId]);

    // Drivers & vehicles for the filter dropdowns
    useEffect(() => {
        axios.get(`/api/drivers/${operatorId}`).then(r => setDrivers(Array.isArray(r.data) ? r.data : [])).catch(() => {});
        axios.get(`/api/vehicles/${operatorId}`).then(r => setVehicles(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    }, [operatorId]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        const from = fromF ? new Date(`${fromF}T00:00:00`) : null;
        const to = toF ? new Date(`${toF}T23:59:59.999`) : null;
        return rows
            .filter(r => {
                if (driverF && idOf(r.driverId) !== driverF) return false;
                if (vehicleF && idOf(r.vehicleId) !== vehicleF) return false;
                if (statusF === 'ongoing' && r.endDate) return false;
                if (statusF === 'ended' && !r.endDate) return false;
                // Date range: keep any assignment period that overlaps the chosen range
                const s = new Date(r.startDate);
                const e = r.endDate ? new Date(r.endDate) : new Date();
                if (from && e < from) return false;
                if (to && s > to) return false;
                if (!q) return true;
                return [dLabel(r.driverId), vLabel(r.vehicleId), r.vehicleId?.make, r.vehicleId?.model, r.startReason, r.endReason, r.notes, r.changedBy]
                    .join(' ').toLowerCase().includes(q);
            })
            .sort((a, b) => new Date(b.startDate) - new Date(a.startDate));
    }, [rows, search, driverF, vehicleF, statusF, fromF, toF]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
    const safePage = Math.min(page, totalPages);
    const pageRows = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);
    const hasFilters = driverF || vehicleF || statusF || search || fromF || toF;
    const clearAll = () => { setDriverF(''); setVehicleF(''); setStatusF(''); setSearch(''); setFromF(''); setToF(''); setPage(1); };

    const exportCSV = () => {
        const head = ['Driver', 'Vehicle', 'Start', 'End', 'Duration', 'Status', 'Start Reason', 'Previous Driver/Vehicle', 'End Reason', 'Next Driver/Vehicle', 'Changed By', 'Notes'];
        const lines = filtered.map(r => [
            dLabel(r.driverId), vLabel(r.vehicleId), `${fmtDate(r.startDate)} ${fmtTime(r.startDate)}`,
            r.endDate ? `${fmtDate(r.endDate)} ${fmtTime(r.endDate)}` : 'Ongoing', duration(r.startDate, r.endDate),
            r.endDate ? 'Ended' : 'Ongoing', r.startReason,
            [r.previousDriverId && `Driver: ${dLabel(r.previousDriverId)}`, r.previousVehicleId && `Vehicle: ${vLabel(r.previousVehicleId)}`].filter(Boolean).join('; '),
            r.endReason || '',
            [r.nextDriverId && `Driver: ${dLabel(r.nextDriverId)}`, r.nextVehicleId && `Vehicle: ${vLabel(r.nextVehicleId)}`].filter(Boolean).join('; '),
            r.changedBy || '', r.notes || ''
        ].map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','));
        const blob = new Blob([[head.join(','), ...lines].join('\n')], { type: 'text/csv;charset=utf-8' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `driver_vehicle_assignment_history_${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
    };

    const field = { height: 38, border: '1px solid #e2e8f0', borderRadius: 10, padding: '0 10px', fontSize: 13, background: 'white', color: '#334155', outline: 'none' };
    const lbl = { fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 5 };
    const th = { textAlign: 'left', padding: '12px 14px', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap', position: 'sticky', top: 0, zIndex: 1 };
    const td = { padding: '12px 14px', borderBottom: '1px solid #f1f5f9', fontSize: 13, color: '#334155', verticalAlign: 'top' };
    const sub = { fontSize: 11.5, color: '#64748b', marginTop: 3 };
    const pbtn = (active, disabled) => ({
        minWidth: 34, height: 34, padding: '0 10px', borderRadius: 8, fontSize: 13, fontWeight: 700,
        border: `1px solid ${active ? '#16539C' : '#e2e8f0'}`, background: active ? '#16539C' : 'white',
        color: active ? 'white' : disabled ? '#cbd5e1' : '#334155', cursor: disabled ? 'not-allowed' : 'pointer'
    });

    const card = { background: 'white', border: '1px solid #e2e8f0', borderRadius: 16, boxShadow: '0 1px 3px rgba(15,23,42,0.04)' };

    return (
        <div style={{ padding: 30, background: '#f8fafc', minHeight: '100vh', fontFamily: "'Inter', sans-serif" }}>
            {/* Page header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                    {onBack && (
                        <button onClick={onBack} title="Back to Driver Vehicle Mapping"
                            style={{ width: 40, height: 40, borderRadius: 12, border: '1px solid #e2e8f0', background: 'white', cursor: 'pointer', fontSize: 18, color: '#334155', marginTop: 2 }}>←</button>
                    )}
                    <div>
                        <h1 style={{ margin: 0, fontSize: 28, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em' }}>Assignment History</h1>
                        <p style={{ margin: '6px 0 0', fontSize: 14, color: '#64748b' }}>Every period a driver was assigned to a vehicle · {filtered.length} records</p>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={exportCSV} style={{ ...field, height: 42, padding: '0 16px', fontWeight: 700, color: '#16a34a', cursor: 'pointer' }} title="Export filtered rows to CSV">Export</button>
                    <button onClick={load} style={{ ...field, height: 42, width: 42, padding: 0, cursor: 'pointer', fontSize: 16 }} title="Refresh">⟳</button>
                </div>
            </div>

            <div style={{ ...card, overflow: 'hidden' }}>
                {/* Filters */}
                <div style={{ padding: '16px 24px', borderBottom: '1px solid #f1f5f9', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, alignItems: 'end', background: '#fcfdff' }}>
                    <div>
                        <div style={lbl}>Driver</div>
                        <select style={{ ...field, width: '100%' }} value={driverF} onChange={e => { setDriverF(e.target.value); setPage(1); }}>
                            <option value="">All Drivers</option>
                            {drivers.map(d => <option key={d._id} value={d._id}>{dLabel(d)}</option>)}
                        </select>
                    </div>
                    <div>
                        <div style={lbl}>Vehicle</div>
                        <select style={{ ...field, width: '100%' }} value={vehicleF} onChange={e => { setVehicleF(e.target.value); setPage(1); }}>
                            <option value="">All Vehicles</option>
                            {vehicles.map(v => <option key={v._id} value={v._id}>{vLabel(v)}</option>)}
                        </select>
                    </div>
                    <div>
                        <div style={lbl}>From Date</div>
                        <input type="date" style={{ ...field, width: '100%' }} value={fromF} max={toF || undefined} onChange={e => { setFromF(e.target.value); setPage(1); }} />
                    </div>
                    <div>
                        <div style={lbl}>To Date</div>
                        <input type="date" style={{ ...field, width: '100%' }} value={toF} min={fromF || undefined} onChange={e => { setToF(e.target.value); setPage(1); }} />
                    </div>
                    <div>
                        <div style={lbl}>Status</div>
                        <select style={{ ...field, width: '100%' }} value={statusF} onChange={e => { setStatusF(e.target.value); setPage(1); }}>
                            <option value="">All Periods</option>
                            <option value="ongoing">Ongoing</option>
                            <option value="ended">Ended</option>
                        </select>
                    </div>
                    <div>
                        <div style={lbl}>Search</div>
                        <input style={{ ...field, width: '100%' }} placeholder="Name, plate, reason…" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
                    </div>
                    {hasFilters && (
                        <div>
                            <button onClick={clearAll} style={{ ...field, width: '100%', fontWeight: 700, color: '#b91c1c', cursor: 'pointer' }}>Clear Filters</button>
                        </div>
                    )}
                </div>

                {/* Table */}
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr>
                                <th style={th}>#</th>
                                <th style={th}>Driver</th>
                                <th style={th}>Vehicle</th>
                                <th style={th}>Assignment Period</th>
                                <th style={th}>Duration</th>
                                <th style={th}>How It Started</th>
                                <th style={th}>How It Ended</th>
                                <th style={th}>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={8} style={{ ...td, textAlign: 'center', padding: 40, color: '#94a3b8' }}>Loading history…</td></tr>
                            ) : pageRows.length === 0 ? (
                                <tr><td colSpan={8} style={{ ...td, textAlign: 'center', padding: 40, color: '#94a3b8' }}>No assignment history found{hasFilters ? ' for these filters' : ''}.</td></tr>
                            ) : pageRows.map((r, i) => (
                                <tr key={r._id}>
                                    <td style={{ ...td, color: '#94a3b8', fontWeight: 600 }}>{(safePage - 1) * pageSize + i + 1}</td>
                                    <td style={td}>
                                        <div style={{ fontWeight: 700, color: '#0f172a' }}>{r.driverId?.name || 'Deleted driver'}</div>
                                        {r.driverId?.driverCode && <div style={sub}>{r.driverId.driverCode}</div>}
                                    </td>
                                    <td style={td}>
                                        <div style={{ fontWeight: 700, color: '#0f172a' }}>{r.vehicleId?.plateNumber || 'Deleted vehicle'}</div>
                                        <div style={sub}>{[r.vehicleId?.vehicleCode, [r.vehicleId?.make, r.vehicleId?.model].filter(Boolean).join(' ')].filter(Boolean).join(' · ')}</div>
                                    </td>
                                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                                        <div><span style={{ color: '#64748b' }}>From</span> <strong>{fmtDate(r.startDate)}</strong> <span style={sub}>{fmtTime(r.startDate)}</span></div>
                                        <div style={{ marginTop: 3 }}><span style={{ color: '#64748b' }}>To</span> {r.endDate ? <><strong>{fmtDate(r.endDate)}</strong> <span style={sub}>{fmtTime(r.endDate)}</span></> : <strong style={{ color: '#059669' }}>Present</strong>}</div>
                                    </td>
                                    <td style={{ ...td, whiteSpace: 'nowrap', fontWeight: 600 }}>{duration(r.startDate, r.endDate)}</td>
                                    <td style={td}>
                                        <div>{r.startReason}</div>
                                        {r.previousDriverId && <div style={sub}>Took over from: <strong>{dLabel(r.previousDriverId)}</strong></div>}
                                        {r.previousVehicleId && <div style={sub}>Previous vehicle: <strong>{vLabel(r.previousVehicleId)}</strong></div>}
                                        {r.changedBy && <div style={sub}>By {r.changedBy}</div>}
                                    </td>
                                    <td style={td}>
                                        {r.endDate ? (
                                            <>
                                                <div>{r.endReason || 'Ended'}</div>
                                                {r.nextDriverId && <div style={sub}>Handed to: <strong>{dLabel(r.nextDriverId)}</strong></div>}
                                                {r.nextVehicleId && <div style={sub}>Moved to: <strong>{vLabel(r.nextVehicleId)}</strong></div>}
                                            </>
                                        ) : <span style={{ color: '#94a3b8' }}>—</span>}
                                    </td>
                                    <td style={td}>
                                        <span style={{ fontSize: 11, fontWeight: 800, padding: '4px 10px', borderRadius: 99, whiteSpace: 'nowrap',
                                            background: r.endDate ? '#f1f5f9' : '#ecfdf5', color: r.endDate ? '#64748b' : '#047857', border: `1px solid ${r.endDate ? '#e2e8f0' : '#a7f3d0'}` }}>
                                            {r.endDate ? 'ENDED' : 'ONGOING'}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                <div style={{ padding: '12px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: '#64748b' }}>
                        <span>
                            {filtered.length === 0 ? 'No records' : `Showing ${(safePage - 1) * pageSize + 1}–${Math.min(safePage * pageSize, filtered.length)} of ${filtered.length}`}
                        </span>
                        <select style={{ ...field, height: 32 }} value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}>
                            {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n} / page</option>)}
                        </select>
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <button style={pbtn(false, safePage === 1)} disabled={safePage === 1} onClick={() => setPage(safePage - 1)}>Prev</button>
                        {pageList(safePage, totalPages).map((p, i) => p === '…'
                            ? <span key={`e${i}`} style={{ padding: '0 4px', color: '#94a3b8' }}>…</span>
                            : <button key={p} style={pbtn(p === safePage, false)} onClick={() => setPage(p)}>{p}</button>)}
                        <button style={pbtn(false, safePage === totalPages)} disabled={safePage === totalPages} onClick={() => setPage(safePage + 1)}>Next</button>
                    </div>
                </div>
            </div>
        </div>
    );
}
